import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button
} from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  getClients,
  importClients,
  uploadClientEtiquetaExcel,
  downloadClientEtiquetaExcel
} from '../services/api';
import CriarCliente from './criarcliente';
import ConfirmModal from '../warehouse/modals/confirmx';
import FeedbackModal from '../warehouse/modals/FeedbackModal';

const ClientesTab = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // modais
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // fetch inicial
  useEffect(() => {
    getClients()
      .then(res => {
        setClients(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // filtrar clientes pela barra de pesquisa
  const filtered = clients.filter(c => {
    const s = searchTerm.toLowerCase();
    return (
      (c.nome2?.toLowerCase().includes(s)) ||
      (c.name?.toLowerCase().includes(s)) ||
      (c.email?.toLowerCase().includes(s))
    );
  });

  // importar do PHC
  const handleImportAPI = () => importClients().then(r => r.data);

  const handleConfirmImport = async () => {
    setConfirmVisible(false);
    setFeedbackVisible(true);
    setIsSubmitting(true);
    setSubmissionComplete(false);
    setErrorMessage(null);

    try {
      await handleImportAPI();
      setSubmissionComplete(true);
      const updated = await getClients();
      setClients(updated.data);
    } catch (err) {
      setErrorMessage(err.message || 'Erro desconhecido');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setFeedbackVisible(false), 2000);
    }
  };

  // upload do Excel de etiqueta
  const handleExcelUpload = async (client, file) => {
    if (!file) return;
    try {
      await uploadClientEtiquetaExcel(client.id, file);
      // refresca a lista para ativar o botão de download
      const updated = await getClients();
      setClients(updated.data);
    } catch (err) {
      console.error('Erro ao anexar Excel:', err);
    }
  };

  // download do Excel de etiqueta
  const handleExcelDownload = async (client) => {
    try {
      const resp = await downloadClientEtiquetaExcel(client.id);
      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${client.nome2 || client.name}_etiquetas.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erro ao fazer download do Excel:', err);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h6">Lista de Clientes</Typography>
        </Grid>
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              <CriarCliente />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setConfirmVisible(true)}
              >
                Atualizar pelo PHC
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Barra de pesquisa */}
      <TextField
        fullWidth
        placeholder="Procurar por cliente"
        variant="outlined"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{ my: 2 }}
      />

      <DataTable
        value={filtered}
        loading={loading}
        paginator
        rows={10}
        dataKey="id"
      >
        <Column field="nome2" header="Abreviatura" sortable />
        <Column field="name" header="Nome" sortable />
        <Column field="email" header="Email" sortable />
        <Column
          header="Ações"
          body={client => (
            client.excel_file
              ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleExcelDownload(client)}
                >
                  Download Excel
                </Button>
              )
              : (
                <>
                  <input
                    accept=".xls,.xlsx"
                    style={{ display: 'none' }}
                    id={`upload-${client.id}`}
                    type="file"
                    onChange={e => handleExcelUpload(client, e.target.files[0])}
                  />
                  <label htmlFor={`upload-${client.id}`}>
                    <Button component="span" size="small" variant="contained">
                      Anexar Excel
                    </Button>
                  </label>
                </>
              )
          )}
        />
      </DataTable>

      <style>{`
        .p-datatable-tbody > tr:hover {
          background-color: #f5f5f5;
        }
      `}</style>

      <ConfirmModal
        visible={confirmVisible}
        message="Deseja atualizar os clientes pelo PHC?"
        onConfirm={handleConfirmImport}
        onCancel={() => setConfirmVisible(false)}
      />

      <FeedbackModal
        open={feedbackVisible}
        isSubmitting={isSubmitting}
        submissionComplete={submissionComplete}
        error={errorMessage}
        onClose={() => setFeedbackVisible(false)}
      />
    </Box>
  );
};

export default ClientesTab;
